import { useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
// actions
import { offersActionCreators } from "../../../common/store/modules/offersModule";
// helpers
import {
  hotelPriceTypeOptions,
  offersStep,
  tooltip,
  PRICE_CATEGORY_IDS,
  getInitialOfferPricesForPriceType,
  isPricingStepReadOnly
} from "../offersHelpers";
// components
import { CustomFormInput } from "@gl-shared/shared/ui";
import { CustomFormDropdown } from "@gl-shared/shared/ui";
import { CustomFormSwitchToggle } from "@gl-shared/shared/ui";

const pricingStep = offersStep.pricing;

const setOfferPriceAt = (prices, index, updates) => {
  const next = [...(prices || [])];
  const existing = next[index] || {
    price_category_id:
      index === 0 ? PRICE_CATEGORY_IDS.Adult : index === 1 ? PRICE_CATEGORY_IDS.Child : PRICE_CATEGORY_IDS.Infant,
    price: null,
    price_additional: null,
    description: ""
  };
  next[index] = { ...existing, ...updates };
  return next;
};

const PricingStep = props => {
  useEffect(() => {
    if (!props.isEdit && !props.pricingData?.vatEdited) {
      props.updateStepAction(pricingStep, { ...props.pricingData, vat: props.hotelVat, vatEdited: true });
    }
  }, []);

  const isSelectedPriceTypePerPerson = () => props.pricingData.price_type?.value === "per_person";
  const prices = props.pricingData?.offer_prices ?? [];
  const base = prices[0];
  const child = prices[1];
  const infant = prices[2];
  const isReadOnly = isPricingStepReadOnly;

  // When editing: show "old pricing" message only if offer has no offer_prices (legacy). When adding, we show the form.
  //const isOfferWithNewPricing = prices.length > 0;

  const updateOfferPrices = newPrices => {
    if (isReadOnly) return;
    props.updateStepAction(pricingStep, { ...props.pricingData, offer_prices: newPrices });
  };

  const handlePriceTypeChange = item => {
    if (isReadOnly) return;
    props.updateStepAction(pricingStep, {
      ...props.pricingData,
      price_type: item,
      offer_prices: getInitialOfferPricesForPriceType(item.value)
    });
  };

  const allowChildren = prices.length >= 2;
  const allowInfants = prices.length >= 3;

  const toggleAllowChildren = () => {
    if (isReadOnly) return;
    if (allowChildren) {
      updateOfferPrices(prices.slice(0, 1));
    } else {
      updateOfferPrices([
        ...prices.slice(0, 1),
        { price_category_id: PRICE_CATEGORY_IDS.Child, price: null, price_additional: null, description: "" }
      ]);
    }
  };

  const toggleAllowInfants = () => {
    if (isReadOnly) return;
    if (allowInfants) {
      updateOfferPrices(prices.slice(0, 2));
    } else {
      // Turning ON infants: keep existing adult + child, add infant
      const newPrices = [...prices];
      if (prices.length < 2) {
        newPrices.push({
          price_category_id: PRICE_CATEGORY_IDS.Child,
          price: null,
          price_additional: null,
          description: ""
        });
      }
      newPrices.push({
        price_category_id: PRICE_CATEGORY_IDS.Infant,
        price: null,
        price_additional: null,
        description: ""
      });
      updateOfferPrices(newPrices);
    }
  };

  return (
    <div className="row">
      {isReadOnly && (
        <div className="col-12">
          <div className="info-message-wrapper">
            <p className="info-message-wrapper__message">
              Pricing configuration is currently unavailable. <br /> <br />
              For more details, please contact GET LOCAL at support@get-local.com.
            </p>
          </div>
        </div>
      )}
      {/* {!isOfferWithNewPricing && props.isEdit ? (
        <div className="info-message-wrapper">
          <p className="info-message-wrapper__message">
            This offer is using the old pricing system and needs to be updated to the new pricing model.
            <br /> <br />
            For more details, please contact GET LOCAL at support@get-local.com.
          </p>
        </div>
      ) : ( */}
      <fieldset disabled={isReadOnly} style={{ display: "contents" }}>
        <div className="col-lg-6 left-side">
          <CustomFormInput
            isDisabled={true}
            name="currency"
            id="currency"
            label="Currency"
            showTooltip
            tooltipTitle={tooltip.currency}
            value={props.hotelCurrency}
            isRequired
            alignRow={true}
          />
          <CustomFormInput
            isDisabled={isReadOnly || props.isEdit}
            type="number"
            min={0}
            name="vat"
            id="vat"
            label="VAT rate in %"
            showTooltip
            tooltipTitle={tooltip.vat}
            value={props.pricingData?.vat && props.pricingData?.vat}
            onInputBlur={value => {
              if (!isReadOnly && !props.isEdit) {
                props.updateStepAction(pricingStep, { ...props.pricingData, vat: value });
              }
            }}
            errorField={props.errorField}
            isRequired
            alignRow={true}
            maskedNumberInput={true}
          />
          <div className="pricing-tab-price-type">
            <CustomFormDropdown
              alignRow={true}
              label="Price type"
              id="price_type"
              selectedItem={props.pricingData.price_type}
              onChange={handlePriceTypeChange}
              errorField={props.errorField}
              placeholder=""
              optionsList={hotelPriceTypeOptions}
              isRequired
              isDisabled={isReadOnly}
              showTooltip
              tooltipTitle={tooltip.priceType}
            />
          </div>
          <>
            <CustomFormInput
              isDisabled={isReadOnly}
              type="number"
              min={0}
              step={1}
              name="base_price"
              id="base_price"
              label="Base price"
              tooltipTitle={tooltip.basePrice}
              value={base?.price != null && base?.price !== "" ? String(base.price) : ""}
              onInputBlur={value =>
                updateOfferPrices(
                  setOfferPriceAt(prices, 0, { price: value !== "" && value != null ? Number(value) : null })
                )
              }
              errorField={props.errorField}
              isRequired={true}
              alignRow={true}
              maskedNumberInput={true}
            />
            <CustomFormInput
              isDisabled={isReadOnly}
              type="number"
              min={0}
              step={1}
              name="additional_unit_price"
              id="additional_unit_price"
              label="Additional unit price"
              tooltipTitle={tooltip.additionalUnitPrice}
              value={
                base?.price_additional != null && base?.price_additional !== "" ? String(base.price_additional) : ""
              }
              onInputBlur={value =>
                updateOfferPrices(
                  setOfferPriceAt(prices, 0, {
                    price_additional: value !== "" && value != null ? Number(value) : null
                  })
                )
              }
              errorField={props.errorField}
              isRequired={true}
              alignRow={true}
              maskedNumberInput={true}
            />
            <CustomFormInput
              isDisabled={isReadOnly}
              maxLength="15"
              type="text"
              name="base_price_description"
              id="base_price_description"
              label="Price description"
              tooltipTitle={tooltip.priceDescription}
              value={base?.description ?? ""}
              onInputChange={event =>
                updateOfferPrices(setOfferPriceAt(prices, 0, { description: event.target.value }))
              }
              errorField={null}
              alignRow={true}
            />
          </>
        </div>
        <div className="col-lg-6 right-side">
          {isSelectedPriceTypePerPerson() && (
            <>
              <div className={isReadOnly ? "pricing-step__control--read-only" : undefined}>
                <CustomFormSwitchToggle
                  tooltipTitle={tooltip.allowChildren}
                  label="Allow children"
                  checked={allowChildren}
                  onChange={toggleAllowChildren}
                />
              </div>
              {allowChildren && (
                <>
                  <CustomFormInput
                    isDisabled={isReadOnly}
                    type="number"
                    min={0}
                    step={1}
                    name="child_price"
                    id="child_price"
                    label="Price per child"
                    tooltipTitle={tooltip.pricePerChild}
                    value={child?.price != null && child?.price !== "" ? String(child.price) : ""}
                    onInputBlur={value =>
                      updateOfferPrices(
                        setOfferPriceAt(prices, 1, { price: value !== "" && value != null ? Number(value) : null })
                      )
                    }
                    errorField={props.errorField}
                    isRequired={true}
                    alignRow={true}
                    maskedNumberInput={true}
                  />
                  <CustomFormInput
                    isDisabled={isReadOnly}
                    type="number"
                    min={0}
                    step={1}
                    name="child_additional_price"
                    id="child_additional_price"
                    label="Additional price per child"
                    tooltipTitle={tooltip.additionalPricePerChild}
                    value={
                      child?.price_additional != null && child?.price_additional !== ""
                        ? String(child.price_additional)
                        : ""
                    }
                    onInputBlur={value =>
                      updateOfferPrices(
                        setOfferPriceAt(prices, 1, {
                          price_additional: value !== "" && value != null ? Number(value) : null
                        })
                      )
                    }
                    errorField={props.errorField}
                    isRequired={false}
                    alignRow={true}
                    maskedNumberInput={true}
                  />
                  <CustomFormInput
                    isDisabled={isReadOnly}
                    maxLength="15"
                    type="text"
                    name="child_price_description"
                    id="child_price_description"
                    label="Child price description"
                    tooltipTitle={tooltip.childPriceDescription}
                    value={child?.description ?? ""}
                    onInputChange={event =>
                      updateOfferPrices(setOfferPriceAt(prices, 1, { description: event.target.value }))
                    }
                    errorField={null}
                    alignRow={true}
                  />

                  <div className={isReadOnly ? "pricing-step__control--read-only" : undefined}>
                    <CustomFormSwitchToggle
                      tooltipTitle={tooltip.allowInfants}
                      label="Allow infants"
                      checked={allowInfants}
                      onChange={toggleAllowInfants}
                    />
                  </div>
                  {allowInfants && (
                    <>
                      <CustomFormInput
                        isDisabled={isReadOnly}
                        type="number"
                        min={0}
                        step={1}
                        name="infant_price"
                        id="infant_price"
                        label="Price per infant"
                        tooltipTitle={tooltip.pricePerInfant}
                        value={infant?.price != null && infant?.price !== "" ? String(infant.price) : ""}
                        onInputBlur={value =>
                          updateOfferPrices(
                            setOfferPriceAt(prices, 2, {
                              price: value !== "" && value != null ? Number(value) : null
                            })
                          )
                        }
                        errorField={props.errorField}
                        isRequired={true}
                        alignRow={true}
                        maskedNumberInput={true}
                      />
                      <CustomFormInput
                        isDisabled={isReadOnly}
                        type="number"
                        min={0}
                        step={1}
                        name="infant_additional_price"
                        id="infant_additional_price"
                        label="Additional price per infant"
                        tooltipTitle={tooltip.additionalPricePerInfant}
                        value={
                          infant?.price_additional != null && infant?.price_additional !== ""
                            ? String(infant.price_additional)
                            : ""
                        }
                        onInputBlur={value =>
                          updateOfferPrices(
                            setOfferPriceAt(prices, 2, {
                              price_additional: value !== "" && value != null ? Number(value) : null
                            })
                          )
                        }
                        errorField={props.errorField}
                        isRequired={false}
                        alignRow={true}
                        maskedNumberInput={true}
                      />
                      <CustomFormInput
                        isDisabled={isReadOnly}
                        maxLength="15"
                        type="text"
                        name="infant_price_description"
                        id="infant_price_description"
                        label="Infant price description"
                        tooltipTitle={tooltip.infantPriceDescription}
                        value={infant?.description ?? ""}
                        onInputChange={event =>
                          updateOfferPrices(setOfferPriceAt(prices, 2, { description: event.target.value }))
                        }
                        errorField={null}
                        alignRow={true}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </fieldset>
    </div>
  );
};

const mapStateToProps = state => ({
  pricingData: state.offers.pricingData
});

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateStepAction: offersActionCreators.updateStepAction
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingStep);
